<script lang="ts">
  import { fly } from "svelte/transition";
  import { router } from "../lib/router.svelte.js";
  import { playlists } from "../lib/stores/playlists.svelte.js";
  import { session } from "../lib/stores/session.svelte.js";
  import { ui } from "../lib/stores/ui.svelte.js";
  import AdminPage from "../pages/AdminPage.svelte";
  import LibraryPage from "../pages/LibraryPage.svelte";
  import ProfilePage from "../pages/ProfilePage.svelte";
  import SetlistPage from "../pages/SetlistPage.svelte";
  import SetlistsPage from "../pages/SetlistsPage.svelte";
  import Icon from "./Icon.svelte";
  import NowBar from "./NowBar.svelte";

  const setlistMatch = $derived(router.match("/setlists/:id"));
  const section = $derived(
    router.path.startsWith("/setlists")
      ? "setlists"
      : router.path.startsWith("/profil") || router.path.startsWith("/verwaltung")
        ? "profile"
        : "library",
  );
  const initial = $derived((session.user?.displayName ?? "?").charAt(0).toUpperCase());

  function go(path: string): void {
    if (router.path !== path) router.go(path);
  }
</script>

<div class="shell" class:wide={ui.wide}>
  {#if ui.wide}
    <nav class="side" aria-label="Hauptnavigation">
      <a class="brand" href="./" onclick={(e) => (e.preventDefault(), go("/"))}>
        <span class="logo" aria-hidden="true">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            ><path d="M4 7h16M4 11h16M4 15h9" /><path d="M19.5 17V5" /><circle
              cx="16.5"
              cy="17"
              r="3"
              fill="currentColor"
            /></svg
          >
        </span>
        <span class="brand-name">Piano Sheets</span>
      </a>
      <div class="nav-group">
        <button
          type="button"
          class="nav"
          aria-current={section === "library" ? "page" : undefined}
          onclick={() => go("/")}
        >
          <Icon name="library" /> Bibliothek
        </button>
        <button
          type="button"
          class="nav"
          aria-current={section === "setlists" ? "page" : undefined}
          onclick={() => go("/setlists")}
        >
          <Icon name="setlist" /> Setlists
        </button>
      </div>
      <div class="lists">
        <div class="lists-head">
          <span class="label">Deine Setlists</span>
          <button
            type="button"
            class="icon-btn small"
            aria-label="Neue Setlist"
            onclick={async () => {
              const id = await playlists.create(`Setlist ${playlists.list.length + 1}`);
              if (id) router.go(`/setlists/${id}`);
            }}><Icon name="plus" size={18} /></button
          >
        </div>
        <div class="lists-scroll scroll">
          {#each playlists.list as playlist (playlist.id)}
            <button
              type="button"
              class="list-item"
              class:on={setlistMatch?.id === playlist.id}
              onclick={() => go(`/setlists/${playlist.id}`)}
            >
              <span class="swatch" style:background={playlist.accentColor}>{playlist.icon}</span>
              <span class="list-text">
                <span class="ellipsis list-name">{playlist.name}</span>
                <span class="t3 list-meta">
                  {playlist.items.length}
                  {playlist.items.length === 1 ? "Stück" : "Stücke"}{playlists.activeId === playlist.id
                    ? " · aktuell"
                    : ""}
                </span>
              </span>
            </button>
          {/each}
        </div>
      </div>
      <button
        type="button"
        class="list-item profile"
        class:on={section === "profile"}
        onclick={() => go("/profil")}
      >
        <span class="avatar">{initial}</span>
        <span class="list-text">
          <span class="list-name">{session.user?.displayName}</span>
          <span class="t3 list-meta">{session.isAdmin ? "Profil & Verwaltung" : "Profil"}</span>
        </span>
      </button>
    </nav>
  {/if}

  <main class="content">
    <div class="page scroll" id="page-scroll">
      {#key section + (setlistMatch?.id ?? "")}
        <div class="page-inner" in:fly={{ y: 12, duration: 360 }}>
          {#if setlistMatch}
            <SetlistPage id={setlistMatch.id ?? ""} />
          {:else if section === "setlists"}
            <SetlistsPage />
          {:else if router.path.startsWith("/verwaltung") && session.isAdmin}
            <AdminPage />
          {:else if section === "profile"}
            <ProfilePage />
          {:else}
            <LibraryPage />
          {/if}
        </div>
      {/key}
    </div>
    <NowBar />
  </main>

  {#if !ui.wide}
    <nav class="tabs" aria-label="Hauptnavigation">
      <button
        type="button"
        class="tab"
        aria-current={section === "library" ? "page" : undefined}
        onclick={() => go("/")}
      >
        <Icon name="library" size={24} /> Bibliothek
      </button>
      <button
        type="button"
        class="tab"
        aria-current={section === "setlists" ? "page" : undefined}
        onclick={() => go("/setlists")}
      >
        <Icon name="setlist" size={24} /> Setlists
        {#if playlists.active}
          {#key playlists.active.items.length}
            <span class="badge">{playlists.active.items.length}</span>
          {/key}
        {/if}
      </button>
      <button
        type="button"
        class="tab"
        aria-current={section === "profile" ? "page" : undefined}
        onclick={() => go("/profil")}
      >
        <Icon name="user" size={24} /> Profil
      </button>
    </nav>
  {/if}
</div>

<style>
  .shell {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--ground);
  }
  .shell.wide {
    flex-direction: row;
  }
  .side {
    width: 256px;
    flex: 0 0 256px;
    padding: calc(22px + var(--safe-top)) 14px 14px calc(14px + var(--safe-left));
    display: flex;
    flex-direction: column;
    gap: 22px;
    min-height: 0;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 10px;
  }
  .logo {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: var(--accent);
    color: var(--on-accent);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .brand-name {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 20px;
    letter-spacing: -0.02em;
  }
  .nav-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .nav {
    display: flex;
    align-items: center;
    gap: 14px;
    height: 46px;
    padding: 0 14px;
    border-radius: var(--r-md);
    font-weight: 700;
    font-size: 15px;
    color: var(--text-2);
    transition:
      background var(--dur-fast),
      color var(--dur-fast),
      transform var(--dur-tap) var(--ease-out);
  }
  .nav:hover {
    color: var(--text);
    background: var(--surface-2);
  }
  .nav:active {
    transform: scale(0.97);
  }
  .nav[aria-current="page"] {
    color: var(--text);
    background: var(--raised);
  }
  .nav[aria-current="page"] :global(svg) {
    color: var(--accent);
  }
  .lists {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-height: 0;
  }
  .lists-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4px 0 10px;
  }
  .lists-scroll {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: 0;
  }
  .small {
    width: 34px;
    height: 34px;
  }
  .list-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 10px;
    border-radius: var(--r-md);
    text-align: left;
    width: 100%;
    transition: background var(--dur-fast);
  }
  .list-item:hover {
    background: var(--surface-2);
  }
  .list-item.on .list-name {
    color: var(--accent);
  }
  .swatch {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    border-radius: var(--r-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
  }
  .list-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .list-name {
    font-weight: 700;
    font-size: 14px;
  }
  .list-meta {
    font-size: 12px;
    font-weight: 600;
  }
  .profile {
    height: 58px;
  }
  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--raised-2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
  }
  .content {
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    background: var(--surface);
  }
  .wide .content {
    margin: 8px 8px 8px 0;
    margin-right: calc(8px + var(--safe-right));
    border-radius: var(--r-xl);
    overflow: hidden;
  }
  .page {
    flex: 1;
    min-height: 0;
  }
  .page-inner {
    min-height: 100%;
  }
  .tabs {
    display: flex;
    padding: 4px 8px calc(8px + var(--safe-bottom));
    background: var(--ground);
    border-top: 1px solid var(--line);
  }
  .tab {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    height: 56px;
    justify-content: center;
    font-size: 11px;
    font-weight: 800;
    color: var(--text-3);
    transition:
      color var(--dur-fast),
      transform var(--dur-tap) var(--ease-out);
  }
  .tab[aria-current="page"] {
    color: var(--text);
  }
  .tab:active {
    transform: scale(0.92);
  }
  .badge {
    position: absolute;
    top: 4px;
    left: calc(50% + 6px);
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: var(--accent);
    color: var(--on-accent);
    font-size: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: bump 0.45s var(--ease-pop);
  }
  @keyframes bump {
    40% {
      transform: scale(1.35);
    }
  }
</style>
